class PreloadQueue {
  private inFlight = new Set<string>();
  private queue: Array<{
    key: string;
    request: (signal: AbortSignal) => Promise<void>;
  }> = [];
  private controllers = new Map<string, AbortController>();
  private readonly maxConcurrent = 4;
  private readonly requestTimeoutMs = 10000;

  async add(key: string, request: (signal: AbortSignal) => Promise<void>) {
    // Skip if already loading or queued
    if (this.inFlight.has(key) || this.queue.some((item) => item.key === key)) {
      return;
    }

    // If at capacity, add to queue
    if (this.inFlight.size >= this.maxConcurrent) {
      this.queue.push({ key, request });
      return;
    }
    this.execute(key, request);
  }

  private async execute(key: string, request: (signal: AbortSignal) => Promise<void>) {
    const controller = new AbortController();
    this.controllers.set(key, controller);
    const timeoutId = setTimeout(() => controller.abort(), this.requestTimeoutMs);
    this.inFlight.add(key);
    try {
      await request(controller.signal);
    } catch {
    } finally {
      clearTimeout(timeoutId);
      this.controllers.delete(key);
      this.inFlight.delete(key);
      this.processQueue();
    }
  }

  private processQueue() {
    if (this.queue.length === 0 || this.inFlight.size >= this.maxConcurrent) {
      return;
    }

    const next = this.queue.shift();
    if (next) {
      this.execute(next.key, next.request);
    }
  }

  cancelAll() {
    this.queue = [];
    this.controllers.forEach((controller) => controller.abort());
    this.controllers.clear();
  }
}

export const preloadQueue = new PreloadQueue();
